import type { Theme, Components } from '@mui/material/styles';

// ----------------------------------------------------------------------

/**
 * O papel de fundo do produto é o CANVAS (creme no claro, quase-preto no
 * escuro), não o branco de `background.default` que o CssBaseline do MUI pinta
 * por padrão.
 *
 * Pintar o `body` aqui, em vez de trocar o token, é de propósito:
 * `background.default` é usado como TINTA e como BORDA em dezenas de lugares
 * (o anel do avatar, a bolha da conversa), então mexer no token repintaria
 * tudo isso junto.
 *
 * O fundo do `body` é o que o navegador propaga para a tela inteira — é ele
 * que aparece no overscroll do celular e na faixa do notch, onde a raiz do
 * dashboard não chega.
 */
/*
 * OBJETOS com a var de CSS, nunca funções `({ theme }) => ...`: o
 * `styleOverrides` do CssBaseline vai direto pro `<Global>` do Emotion, que
 * só resolve função no NÍVEL DE CIMA — um valor-função dentro do objeto é
 * serializado como texto e vira uma declaração inválida no CSS
 * (`body:({ theme })=>({...`), silenciosamente descartada pelo navegador.
 * Foi assim que o `body` seguiu branco por baixo do notch mesmo "sobrescrito".
 */
const MuiCssBaseline: Components<Theme>['MuiCssBaseline'] = {
  styleOverrides: {
    /*
     * No `html` TAMBÉM, e não só no `body`: quem pinta a tela inteira é o
     * canvas, e o canvas só herda do `body` por PROPAGAÇÃO — que vale apenas
     * enquanto o `html` é transparente e não cobre a área fora da página
     * (a faixa do notch com `viewport-fit: cover`). Declarado aqui, o canvas
     * tem cor própria e a faixa deixa de cair no branco padrão do navegador.
     *
     * O `MuiCssBaseline` de `with-settings` também declara `html` (o
     * `fontSize`); dois objetos se fundem no `createTheme`.
     */
    html: { backgroundColor: 'var(--palette-background-canvas)' },
    body: { backgroundColor: 'var(--palette-background-canvas)' },
    /*
     * BOTÃO HERDA A COR DO TEXTO.
     *
     * A folha do navegador dá cor própria ao `<button>`, e no Safari essa cor é
     * o azul de sistema — não o preto do Chrome e do Firefox. Todo botão nosso
     * que desenha a própria casca (`appearance: none`) e confia a tinta ao
     * conteúdo aparecia certo em três navegadores e azul no quarto: era o caso
     * dos chips do cabeçalho, onde só o texto SEM cor explícita ficava azul e o
     * texto apagado, que declara a sua, continuava cinza.
     *
     * A correção é o reset clássico, e não uma cor por componente: qualquer
     * botão novo cairia na mesma armadilha.
     */
    button: { color: 'inherit' },
  },
};

// ----------------------------------------------------------------------

export const cssBaseline = { MuiCssBaseline };
